import { Box, Button, Grid, IconButton, Paper } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import MaterialTable from "material-table";
import React, { useCallback, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { toImageUrl } from "../../../../utils/formatHelper";
import FormBannerHome from "./components/FormBannerHome";
import { getBannerColumns, initialBannerTableOptions } from "./helpers/homebannerOption";
import { initialBannerValues } from "./helpers/homeBannerEvtOp";
import {
    createHomeBanner,
    deleteHomeBannerById,
    getAllHomeBanner,
    updateHomeBannerById,
} from "../../../../api/homeBannerApi";
import { uniqueId } from "lodash";
import { uploadImageFile } from "../../../../api/uploadApi";
import { swalCondition } from "../../../../utils/swalHelper";
import { useSelector } from "react-redux";

export default function HomeBanner() {
    // ref
    const controllerRef = useRef(null);
    const bannerTableRef = useRef(null);

    // global redux state
    const { thisMenuPermission } = useSelector(state => {
        const { menuPermission } = state?.menu;
        return {
            thisMenuPermission: menuPermission?.find(menu => menu?.path === window.location.pathname)?.permission,
        };
    });

    // component state
    const [bannerTableOptions, setBannerTableOptions] = useState(initialBannerTableOptions);
    const [initailBannerFormValues, setInitailBannerFormValues] = useState(initialBannerValues);
    const [isOpenFormBanner, setIsOpenFormBanner] = useState(false);
    const [isBannerCreateMode, setIsBannerCreateMode] = useState(false);

    // upload image logic
    const handleUploadImage = useCallback(async imageArray => {
        try {
            let imagePathArray = [];
            for await (const image of imageArray) {
                if (image?.file) {
                    const formData = new FormData();
                    formData.append("image", image?.file);
                    const imagePath = await uploadImageFile(formData, controllerRef.current?.signal);
                    imagePathArray.push(imagePath);
                } else {
                    imagePathArray.push(image?.name);
                }
            }
            return imagePathArray;
        } catch (error) {
            console.dir(error);
        }
    }, []);

    /** Start Banner Tab */
    // fetch banner data
    const fetchBannerData = useCallback(async query => {
        try {
            const queryParams = {
                page: query?.page + 1 || 1,
                limit: query?.pageSize || 10,
                search: query?.search || "",
                order: query?.orderBy?.field || "homeBannerId",
                direction: query?.orderBy?.field ? query?.orderDirection : "desc",
            };
            const res = await getAllHomeBanner(queryParams, controllerRef.current?.signal);
            if (res) {
                setBannerTableOptions(currentState => ({
                    ...currentState,
                    currentPage: query?.page,
                    pageSize: query?.pageSize || 10,
                    totalData: res?.pagination?.totalData || 0,
                    order: query?.orderBy?.field || "timestamp",
                    direction: query?.orderBy?.field ? query?.orderDirection : "desc",
                }));
                return {
                    data: res?.data || [],
                    page: query?.page || 0,
                    totalCount: res?.pagination?.totalData || 0,
                };
            }
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        } catch (error) {
            console.dir(error);
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        }
    }, []);

    // set formik banner value
    const setBannerFormValues = useCallback(homeBanner => {
        setInitailBannerFormValues({
            homeBannerId: homeBanner?.homeBannerId,
            bannerImages: homeBanner?.image
                ? [
                      {
                          id: uniqueId(),
                          name: homeBanner?.image,
                          src: toImageUrl(homeBanner?.image),
                      },
                  ]
                : [],
            bannerStartDate: homeBanner?.startDate ? new Date(homeBanner?.startDate) : new Date(),
            bannerEndDate: homeBanner?.endDate ? new Date(homeBanner?.endDate) : new Date(),
            bannerStatus: homeBanner?.status,
        });
    }, []);

    // clear formik banner value
    const clearBannerFormValues = useCallback(() => {
        setInitailBannerFormValues({
            bannerImages: [],
            bannerStartDate: new Date(),
            bannerEndDate: new Date(),
            bannerStatus: true,
        });
    }, []);

    // open modal create banner form
    const handleOpenCreateBannerForm = useCallback(() => {
        clearBannerFormValues();
        setIsBannerCreateMode(true);
        setIsOpenFormBanner(true);
    }, [clearBannerFormValues]);

    // open modal edit banner form
    const handleOpenEditBannerForm = useCallback(
        homeBanner => {
            setBannerFormValues(homeBanner);
            setIsBannerCreateMode(false);
            setIsOpenFormBanner(true);
        },
        [setBannerFormValues],
    );

    // close modal banner form
    const handleCloseBannerForm = useCallback(() => {
        clearBannerFormValues();
        setIsBannerCreateMode(false);
        setIsOpenFormBanner(false);
    }, [clearBannerFormValues]);

    // create banner logic
    const handleCreateHomeBanner = useCallback(
        async banner => {
            try {
                let bannerImagePath = "";
                if (banner?.bannerImages?.length) {
                    const imagePathArray = await handleUploadImage(banner?.bannerImages);
                    bannerImagePath = imagePathArray[0];
                }
                const createBody = {
                    image: bannerImagePath,
                    startDate: banner?.bannerStartDate,
                    endDate: banner?.bannerEndDate,
                    status: banner?.bannerStatus,
                };
                const isCreateBanner = await createHomeBanner(createBody, controllerRef.current?.signal);
                if (isCreateBanner) {
                    bannerTableRef.current.onQueryChange();
                    setIsOpenFormBanner(false);
                }
            } catch (error) {
                console.dir(error);
            }
        },
        [handleUploadImage],
    );

    // update banner logic
    const handleUpdateHomeBanner = useCallback(
        async banner => {
            try {
                let bannerImagePath = "";
                if (banner?.bannerImages?.length) {
                    const imagePathArray = await handleUploadImage(banner?.bannerImages);
                    bannerImagePath = imagePathArray[0];
                }
                const updateBody = {
                    image: bannerImagePath,
                    startDate: banner?.bannerStartDate,
                    endDate: banner?.bannerEndDate,
                    status: banner?.bannerStatus,
                };
                const isUpdateBanner = await updateHomeBannerById(
                    banner?.homeBannerId,
                    updateBody,
                    controllerRef.current?.signal,
                );
                if (isUpdateBanner) {
                    bannerTableRef.current.onQueryChange();
                    setIsOpenFormBanner(false);
                }
            } catch (error) {
                console.dir(error);
            }
        },
        [handleUploadImage],
    );

    // change banner status
    const handleChangeBannerStatus = useCallback(async ({ checked, id }) => {
        try {
            const isUpdate = await updateHomeBannerById(id, { status: checked }, controllerRef.current?.signal);
            if (isUpdate) {
                bannerTableRef.current.onQueryChange();
            }
        } catch (error) {
            console.dir(error);
        }
    }, []);

    // delete banner logic
    const handleDeleteHomeBanner = useCallback(async data => {
        const isConfirm = await swalCondition("<div class='h3 t-text-primary'>Are you delete?</div>", "", {
            icon: "error",
        });
        if (isConfirm) {
            const isDelete = await deleteHomeBannerById(data?.homeBannerId, controllerRef.current?.signal);
            if (isDelete) {
                bannerTableRef.current.onQueryChange();
            }
        }
    }, []);
    /** Start Banner Tab */

    return (
        <div>
            <Grid container spacing={3}>
                <Grid item xs={12} className="mb-3">
                    <h1 className="t-text-primary">Banner Home Setting</h1>
                </Grid>

                <Grid item xs={12} className="mb-3">
                    <Paper className="p-lg-10 p-5">
                        {thisMenuPermission?.create && (
                            <Grid container spacing={3} justify={"flex-end"}>
                                {/* <Grid item xs={12} md={7} xl={6}>
                                <FormSearch onSubmit={handleClickSearch} onClear={handleClickClear} />
                            </Grid> */}
                                <Grid item xs={12} md={5} xl={6}>
                                    <Box className="row align-items-center justify-content-lg-end">
                                        <div className="col-6 col-xxl-4">
                                            <Button
                                                onClick={handleOpenCreateBannerForm}
                                                className="w-100 btn t-btn-primary">
                                                <i className="fas fa-plus" style={{ color: "inherit" }}></i>
                                                Add
                                            </Button>
                                        </div>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                        {/* table */}
                        <Grid container>
                            <Grid item xs={12}>
                                <div className="t-table-custom">
                                    <MaterialTable
                                        tableRef={bannerTableRef}
                                        components={{ Container: Box }}
                                        title=""
                                        data={fetchBannerData}
                                        columns={getBannerColumns({ handleChangeBannerStatus, thisMenuPermission })}
                                        options={bannerTableOptions}
                                        actions={[
                                            {
                                                icon: "edit",
                                                iconProps: { className: "action-edit" },
                                                tooltip: "Edit",
                                                onClick: (event, rowData) => handleOpenEditBannerForm(rowData),
                                                hidden: !thisMenuPermission?.edit,
                                            },
                                            {
                                                icon: "delete",
                                                iconProps: { className: "action-delete" },
                                                tooltip: "Delete",
                                                onClick: (event, rowData) => handleDeleteHomeBanner(rowData),
                                                hidden: !thisMenuPermission?.delete,
                                            },
                                        ]}
                                    />
                                </div>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Modal */}
                <Grid item xs={12}>
                    <Modal scrollable={true} show={isOpenFormBanner} size="lg" onHide={handleCloseBannerForm}>
                        <Modal.Header>
                            <Modal.Title>{isBannerCreateMode ? "Add" : "Edit"} Banner</Modal.Title>
                            <IconButton onClick={handleCloseBannerForm}>
                                <Close />
                            </IconButton>
                        </Modal.Header>
                        <Modal.Body>
                            <FormBannerHome
                                initialFormValues={initailBannerFormValues}
                                onSubmit={isBannerCreateMode ? handleCreateHomeBanner : handleUpdateHomeBanner}
                                onCancel={handleCloseBannerForm}
                            />
                        </Modal.Body>
                    </Modal>
                </Grid>
            </Grid>
        </div>
    );
}
